/*
 * Copyright (c) 2023 CalAmp Corp.  All Rights Reserved
 */
import isNil from 'lodash.isnil';
import {getDriverScores} from '@ion/applications/src/pages/admin/driverscoring/DriverScoreUtil';
import {getAssetState} from '@ion/core/src/entities/Asset';
import {translate} from '@ion/core/src/locale/locale';
import UserPreferences from '@ion/core/src/preferences/UserPreferences';
import {
  Account,
  CVSAssetType,
  DateFilterOptions,
  GenericObject,
  HVSRecord,
  Report,
  SavedReport
} from '@ion/core/src/types/entities';
import {calculateDistance} from '@ion/core/src/utils/DistanceUtils';
import {IonGridAPI} from '@ion/dls/src/grid/IonGrid';
import {calculateTimeDiff} from '@ion/dls/src/utils/TableUtils';

type AugmentedReport = Report & SavedReport;

// defining the arguments for getParams utility...
interface ParamsArg {
  account?: Account;
  dateFilterOptions?: DateFilterOptions;
}

// defining report params...
enum ReportParams {
  speedingOneToFour = 'Speed0',
  speedingFiveToNine = 'Speed1',
  speedingTenToFourteen = 'Speed2',
  speedingFifteenPlus = 'Speed3',
  nightTimeDriving = 'Night_Weight',
  harshAcceleration = 'Harsh_Acceleration_Weight',
  harshDeceleration = 'Harsh_Braking_Weight',
  hardCornering = 'Hard_Cornering_Weight',
  visionCollisionWarning = 'Vision_Collision_Warning',
  visionDistractedDriving = 'Vision_Distracted_Driving',
  visionTrafficViolation = 'Vision_Traffic_Violation',
  visionLaneDrift = 'Vision_Lane_Drift',
  visionLaneDeparture = 'Vision_Lane_Departure',
  paramInterval = 'param_Interval',
  startDate = 'Start_Date',
  endDate = 'end_date'
};

// defines the getScoresParams method's return type...
type ScoreParams = {
  [key in ReportParams]: number;
};

// defines the report tab...
export interface ReportsTabItem {
  id: string;
  reportURL: string;
  report: Report;
  mode: ReportTabMode;
  count?: number;
};

export interface ResolveReportTabParams {
  report?: Report;
  reportURL?: string;
  mode: ReportTabMode;
  reportTabs?: ReportsTabItem[];
};

export enum ReportsMode {
  AssetHistory = 'AssetHistory',
  ReportsDashboard = 'ReportsDashboard',
  ShipmentHistory = 'ShipmentHistory'
};

// defines the report tab mode...
export enum ReportTabMode {
  AssetHistory = 'AssetHistory',
  ShipmentHistory = 'ShipmentHistory',
  JasperReport = 'JasperReport',
  Default = 'Default',
  Schedule = 'Schedule'
};

export interface ReportOperations {
  hasAddPermission?: boolean;
  hasUpdatePermission?: boolean;
  isRefresh?: boolean;
  hideScheduledInfo?: boolean;
};

/**
 * @augmentSavedReports
 *
 * Returns a boolean value of whether or not the mode
 * passed is equal to the AssetHistory enum value
 */
export const augmentSavedReports = (reports: SavedReport[]): AugmentedReport[] => {
  const augmented = reports.map(report => {
    const {
      uri,
      label
    } = report;
    return {
      ...report,
      categoryName    : '',
      jasperReportUrl : uri,
      name            : label
    };
  });
  return augmented;
};

/**
 * @isAssetHistoryMode
 *
 * Returns a boolean value of whether or not the mode
 * passed is equal to the AssetHistory enum value
 */
export const isAssetHistoryMode = (mode?: ReportsMode): boolean => {
  return mode === ReportsMode.AssetHistory;
};

/**
 * @getHarshEvents
 *
 * compares a list of events against our events list and filters
 * the passed list down to ones that contain harsh events
 */
export const getHarshEvents = (events: HVSRecord[] = []): HVSRecord[] => {
  const harshEvents = events.filter((event: HVSRecord): boolean => {
    return event.isSevereEvent;
  });
  return harshEvents;
};

/**
 * @getEventWithEngineAndIdleHours
 * @param events An array of event objects.
 * @returns the first event from events that has both engineHours and idleHours properties.
 * @description
 * This function returns the first event from events that has values for both engineHours and idleHours properties.
 */
const getEventWithEngineAndIdleHours = (events: HVSRecord[]) => {
  return events.find(event => event.engineHours && !isNil(event.idleHours));
};

/**
 * @getIdlingTime
 * @param eventsByAsset: An object containing arrays of events, with the keys being the asset ID.
 * @returns a number representing the accumulated idle time in hours of the assets.
 * @description
 * This function calculates the idle time of assets by iterating through the arrays of events
 * of each asset in eventsByAsset.
 *
 * This function calculates the idle time of assets by iterating through the arrays of events
 *  of each asset in eventsByAsset.
 *
 * For each asset, the first and last events with engine and idle hours information are
 * extracted by calling getEventWithEngineAndIdleHours on the events and the reversed events.
 * The idle time between these events is calculated in minutes by calling getIdleTimeInMinutes
 * with firstEvent and lastEvent as arguments. This idle time is then added to the idleTimeAccumulator
 * and converted to hours.
 *
 *Finally, the accumulated idle time in hours is returned as the output of the function.
 */
export const getIdlingTime = (eventsByAsset: GenericObject): number => {
  let idleTimeAccumulator = 0;
  for (const [asset, events] of Object.entries(eventsByAsset)) {
    const firstEvent = getEventWithEngineAndIdleHours(events);
    const lastEvent = getEventWithEngineAndIdleHours(events.reverse());
    if (firstEvent && lastEvent) {
      idleTimeAccumulator += (getIdleTimeInMinutes(firstEvent, lastEvent) / 60);
    }
  }
  return idleTimeAccumulator;
};

const getIdleTimeInMinutes = (firstEvent, lastEvent) => {
  return Math.abs((firstEvent.idleHours - lastEvent.idleHours));
};

/**
 * @getDrivingTimeByEngineHoursInMinutes
 * @param firstEvent: An event object with engineHours and idleHours properties.
 * @param lastEvent: An event object with engineHours and idleHours properties.
 * @returns  a number representing the driving time in minutes between firstEvent
 * and lastEvent as calculated from their engineHours and idleHours properties.
 * @description
 * This function calculates the driving time between two events, firstEvent and
 * lastEvent, by taking the absolute difference between their engineHours properties
 * and subtracting the idle time between these events as calculated by
 * getIdleTimeInMinutes. The result is returned as the output in minutes.
 */
const getDrivingTimeByEngineHoursInMinutes = (firstEvent, lastEvent) => {
  const engineHours = Math.abs((firstEvent.engineHours - lastEvent.engineHours));
  const idleHours = getIdleTimeInMinutes(firstEvent, lastEvent);
  return (engineHours - idleHours) / 60;
};

/**
 * @getDrivingTime
 * @param eventsByAsset: An object containing arrays of events, with the keys being the asset name.
 * @returns a number representing the accumulated driving time in hours of the assets.
 * @description
 * This function calculates the driving time of assets by iterating through the
 * arrays of events of each asset in eventsByAsset.
 *
 * For each asset, the first and last events that have engine and idle hours
 * information (determined by calling getEventWithEngineAndIdleHours),
 * the driving time is calculated by calling getDrivingTimeByEngineHoursInMinutes
 * with firstEvent and lastEvent as arguments. This driving time
 * is then added to the driveTimeAccumulator.
 *
 * Finally, the accumulated driving time in hours is returned as the output of the function. Note that
 * the result is returned as the absolute value of driveTimeAccumulator with Math.abs function.
 */
export const getDrivingTime = (eventsByAsset: GenericObject): number => {
  let driveTimeAccumulator = 0;
  for (const [asset, events] of Object.entries(eventsByAsset)) {
    const firstEvent = getEventWithEngineAndIdleHours(events);
    const lastEvent = getEventWithEngineAndIdleHours(events.reverse());
    if (firstEvent && lastEvent) {
      driveTimeAccumulator += getDrivingTimeByEngineHoursInMinutes(firstEvent, lastEvent);
    }
  }
  return Math.abs(driveTimeAccumulator);
};

/**
 * @getDistanceTraveled
 *
 * Calculates the total distance traveled of an asset based on the
 * list by taking the most recent event odometer and subtracting
 * the first event odometer reading.
 */
export const getDistanceTraveled = (historyByAsset?: GenericObject): number => {
  let totalDistance = 0;
  if (historyByAsset) {
    for (const [asset, events] of Object.entries(historyByAsset)) {
      if (events.length >= 2) {
        const convertedEvents = events.map(e => (e)?.gpsOdometer);
        const assetDistance = calculateDistance(convertedEvents);
        totalDistance += assetDistance;
      }
    }
  }
  return totalDistance;
};

/**
 * @makeDurationList
 *
 * Helper function to make a list in memory that tracks
 * an event duration for an item by comparing it to its
 * predecessor
 */
export const makeDurationList = (list?: CVSAssetType[]): {
  duration: {
    hours: number;
    minutes: number;
  },
  id: number;
}[] => {
  const durationList = [];
  list?.forEach((item, i) => {
    const diff = calculateTimeDiff(
      list[i + 1], item || null
    );
    durationList.push({
      id       : item.OBJECTID,
      duration : diff
    });
  });
  return durationList;
};

/**
 * @getFleetOutlookPreferences
 *
 * Returns the fleetOutlook preference if the user has, else returns false.
 */
export function getFleetOutlookPreferences() {
  const store = UserPreferences.getStore();
  return store?.fleetOutlook ?? false;
}

/**
 * @hasValidFOSettings
 *
 * Helper function to validate given is FleetOutlook parameters.
 */
export const hasValidFOSettings = (fleetOutlook: GenericObject) => {
  if (fleetOutlook) {
    const {environment, enterpriseCode, userId} = fleetOutlook;
    return !!(environment.length && enterpriseCode.length && userId.length);
  }
  return false;
};

/**
 * The utility takes an account as a input and returns the key & value pair of params that
 * required to pass to the Driver scorecard V2 reports.
 *
 * ```typescript
 *
 *  import {getScoresParams} from './reportUtils';
 *
 *  const scoreParams = getScoresParams({account});
 *
 * ```
 */
export const getScoresParams = ({account}: ParamsArg): ScoreParams => {
  const scores = getDriverScores(account);
  const params = {} as ScoreParams;
  Object.entries(scores)?.forEach(([key, value]) => {
    params[ReportParams[key]] = value;
  });
  return params;
};

/**
 * The utility function takes a date filter options as a input and return the date params
 * that are required to pass to the Jasper reports.
 *
 * ```typescript
 *
 *  import {getDateParams} from './reportUtils';
 *
 *  const dateParams = getDateParams({dateFilterOptions});
 *
 * ```
 */
export const getDateParams = ({dateFilterOptions}: ParamsArg) => {
  if (dateFilterOptions) {
    return ({
      [ReportParams.paramInterval] : 'Date_Range',
      [ReportParams.startDate]     : dateFilterOptions.startDate,
      [ReportParams.endDate]       : dateFilterOptions.endDate
    });
  }
};

/**
 * The utility function takes a date history and event trip flag as a input and return the total
 * number of assets and events that are actually showing in grid/map. The return array contains
 * total asset count in index 0 and total event count in index 1.
 *
 * ```typescript
 *
 *  import {getTotalAssetAndEvents} from './reportUtils';
 *
 *  const {totalAssets, totalEvents} = getTotalAssetAndEvents(history, true);
 *
 * ```
 */
export const getTotalAssetAndEvents = (history, showEventsBetweenTrips = false) => {
  const assetMap  = {};
  let assetsCount = 0;
  let eventsCount = 0;
  const isCountEvent = (tripGroup: number | undefined) => tripGroup || showEventsBetweenTrips;

  history?.forEach(({assetId, tripGroup, messageUuid, gridVisibility}) => {
    if (gridVisibility) {
      const asset = assetMap[assetId];
      if (!asset) {
        assetsCount++;
        isCountEvent(tripGroup) && eventsCount++;
        assetMap[assetId] = [messageUuid];
      } else if (asset && !asset.includes(messageUuid)) {
        asset.push(messageUuid);
        isCountEvent(tripGroup) && eventsCount++;
      }
    }
  });
  return [assetsCount, eventsCount];
};

// helper method helps to determines  mode...
const isMode = (reportMode: ReportTabMode) => (mode: ReportTabMode) => mode === reportMode;

// determines whether the passing mode is view or not...
export const isDefaultMode = isMode(ReportTabMode.Default);

// determines whether the passing mode is jasper report or not...
export const isJasperReportMode = isMode(ReportTabMode.JasperReport);

// determines whether the passing mode is jasper schedule mode or not...
export const isJasperScheduleMode = isMode(ReportTabMode.Schedule);

// determines whether the passing mode is asset history mode or not...
export const isAHRMode = isMode(ReportTabMode.AssetHistory);

// determines whether the passing mode is shipment history mode or not...
export const isSHRMode = isMode(ReportTabMode.ShipmentHistory);

/**
 * The utility provides the option to the client to create a report tab that in
 * the required format. This can be a return tab object based on the report mode.
 *
 * ```typescript
 *
 *  import {resolveReportTab} from './reportUtils';
 *
 *  const tab = resolveReportTab({
 *    mode      : ReportTabMode.Default,
 *    reportURL : null,
 *    report,
 *    reportTabs
 *  )};
 *
 * ```
 */
export const resolveReportTab = ({
  report,
  reportTabs,
  reportURL,
  mode
}: ResolveReportTabParams): ReportsTabItem => {
  const getCount = comparator => {
    const exisiting = reportTabs?.filter(comparator) || [];
    return (exisiting[exisiting.length - 1]?.count ?? 0) + 1;
  };

  if (mode === ReportTabMode.JasperReport) {
    const count = getCount((tab) => tab.report.id === report?.id);
    const id    = report?.id ? `${report.id}_${count}` : `${report.jasperReportUrl}_${count}`;
    return {
      report : {...report, name: `${report.name} ${count}`},
      id,
      reportURL,
      count,
      mode
    };
  } else if (mode === ReportTabMode.AssetHistory) {
    return {
      id        : ReportTabMode.AssetHistory,
      report    : {name: translate('reports:breadcrumbs.assetHistory')} as Report,
      reportURL : null,
      count     : 1,
      mode
    };
  } else if (mode === ReportTabMode.ShipmentHistory) {
    return {
      id        : ReportTabMode.ShipmentHistory,
      report    : {name: translate('reports:breadcrumbs.shipmentHistory')} as Report,
      reportURL : null,
      count     : 1,
      mode
    };
  }
  const count = getCount((tab) => isDefaultMode(tab.mode));
  return {
    id        : `default_${count}`,
    report    : {name: `${translate('reports:name')} ${count}`} as Report,
    reportURL : null,
    count,
    mode
  };
};

/**
 * Handle hover or click events that come from the map and highlight the respective row.
 * The setSelected method provides us with a second argument that, if true, clears any
 * other selection states so we want to do that on hover, but persist a selection state
 * for a row that is clicked.
 */
export const handleMapHoverOrClick = (gridApi: IonGridAPI, hoveredEvent: HVSRecord) => {
  const {isMapClick, OBJECTID} = hoveredEvent;
  let row                      = gridApi.getRowNode(OBJECTID);
  if (row) {
    const {parent} = row;
    // highlight the row in grid
    row.setSelected(true, !isMapClick);
    if (isMapClick) {
      const {expanded : isRowGroupExpanded} = parent || {};
      // expand the highlighted row's group if it's collapsed
      if (!isRowGroupExpanded) {
        parent?.setExpanded?.(true);
      }
      // update the row by the latest node
      row = gridApi.getRowNode(row.data?.OBJECTID);
      // make sure the node is visible in the grid(auto scroll)
      gridApi.ensureNodeVisible(row, 'middle');
    }
  }
};
